'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Clock, Download, Star, Film, Tv, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ContentItem {
  id: string;
  title: string;
  slug: string;
  year: number;
  imdb_rating: number;
  genre: string;
  poster_url: string;
  download_count: number;
  created_at: string;
  type: 'movie' | 'series';
}

const TABS = [
  { label: 'All', value: 'all' },
  { label: 'Movies', value: 'movie' },
  { label: 'Series', value: 'series' },
];

export default function LatestContent() {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'movie' | 'series'>('all');
  const supabase = createClient();

  useEffect(() => {
    const fetchLatest = async () => {
      setLoading(true);
      const [moviesRes, seriesRes] = await Promise.all([
        supabase
          .from('movies')
          .select('id, title, slug, year, imdb_rating, genre, poster_url, download_count, created_at')
          .order('created_at', { ascending: false })
          .limit(40),
        supabase
          .from('series')
          .select('id, title, slug, year, imdb_rating, genre, poster_url, created_at')
          .order('created_at', { ascending: false })
          .limit(40),
      ]);

      const movies: ContentItem[] = (moviesRes.data ?? []).map((m) => ({ ...m, type: 'movie' as const, download_count: m.download_count ?? 0 }));
      const series: ContentItem[] = (seriesRes.data ?? []).map((s) => ({ ...s, type: 'series' as const, download_count: 0 }));

      const combined = [...movies, ...series].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setItems(combined);
      setLoading(false);
    };
    fetchLatest();
  }, []);

  const filtered = activeTab === 'all' ? items : items.filter((i) => i.type === activeTab);

  const getHref = (item: ContentItem) =>
    item.type === 'movie'
      ? `/movie-detail-page?slug=${item.slug}`
      : `/series/${item.slug}`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Clock size={18} className="text-primary" />
          </div>
          <h1 className="text-3xl font-extrabold text-gradient-cyan">Latest</h1>
        </div>
        <p className="text-muted-foreground font-body text-sm">
          {loading ? 'Loading...' : `${filtered.length} recently added titles`}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8">
        {TABS.map((tab) => (
          <button
            key={`tab-${tab.value}`}
            onClick={() => setActiveTab(tab.value as typeof activeTab)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
              activeTab === tab.value
                ? 'neon-border-cyan text-primary bg-primary/10' :'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="glass-card border border-border rounded-2xl p-16 flex flex-col items-center justify-center gap-4">
          <Loader2 size={32} className="animate-spin text-primary" />
          <p className="text-sm text-muted-foreground font-body">Loading latest content...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center border border-border">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Clock size={28} className="text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">No content yet</h3>
          <p className="text-sm text-muted-foreground font-body">
            Content added from the admin dashboard will appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
          {filtered.map((item) => (
            <Link key={`latest-${item.id}`} href={getHref(item)} className="block group">
              <div className="glass-card glass-card-hover rounded-xl overflow-hidden cursor-pointer">
                <div className="relative aspect-[2/3] overflow-hidden">
                  <AppImage
                    src={item.poster_url || '/assets/images/no_image.png'}
                    alt={`${item.title} — ${item.genre} ${item.year}`}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-70" />

                  {/* Type badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm ${
                      item.type === 'movie' ?'bg-blue-500/20 text-blue-400 border border-blue-500/30' :'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    }`}>
                      {item.type === 'movie' ? <Film size={9} /> : <Tv size={9} />}
                      {item.type === 'movie' ? 'Movie' : 'Series'}
                    </span>
                  </div>

                  {/* Rating */}
                  {item.imdb_rating && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                      <Star size={10} className="rating-star fill-yellow-400" />
                      <span className="text-xs font-bold font-mono-data">{item.imdb_rating?.toFixed(1)}</span>
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 neon-btn-solid-cyan rounded-full text-xs font-bold">
                        <Download size={12} />
                        View
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3">
                  <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground font-mono-data">{item.year}</span>
                    {item.genre && (
                      <span className="text-[10px] text-muted-foreground truncate max-w-[70px]">{item.genre}</span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/60 mt-1">{formatDate(item.created_at)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
